import { Global, withEmotionCache } from '@emotion/react';
import { PropsWithChildren, useContext, useEffect, useRef } from 'react';
import { Links, Meta, Scripts, ScrollRestoration } from 'react-router';

import ClientStyleContext from './styles/client.context';
import ServerStyleContext from './styles/server.context';

type DocumentProps = PropsWithChildren & {
  title?: string;
};

const Document = withEmotionCache(({ children }: DocumentProps, emotionCache) => {
  const serverStyleData = useContext(ServerStyleContext);
  const clientStyleData = useContext(ClientStyleContext);
  const reinjectStylesRef = useRef(true);

  useEffect(() => {
    if (!reinjectStylesRef.current) {
      return;
    }

    emotionCache.sheet.container = document.head;

    const tags = emotionCache.sheet.tags;
    emotionCache.sheet.flush();
    tags.forEach((tag) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (emotionCache.sheet as any)._insertTag(tag);
    });

    clientStyleData.reset();

    reinjectStylesRef.current = false;
  }, [clientStyleData, emotionCache.sheet]);

  // Load Google Tag Manager only after hydration. Injecting it during SSR (an
  // inline <script> in <head>) let gtm.js mutate the DOM before React's deferred
  // whole-document hydration ran, causing structural mismatches (React #418).
  // Running it here means GTM touches the DOM only once React already owns it.
  useEffect(() => {
    const gtmId = import.meta.env.VITE_GTM_ID;
    if (!gtmId || document.getElementById('gtm-script')) {
      return;
    }

    const w = window as typeof window & { dataLayer?: Record<string, unknown>[] };
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

    const script = document.createElement('script');
    script.id = 'gtm-script';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
    document.head.appendChild(script);
  }, []);

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="format-detection" content="telephone=no, date=no, email=no, address=no" />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
        <Meta />
        <Links />
        <Global
          styles={{
            'html, body': {
              height: '100%',
            },
          }}
        />

        {serverStyleData?.map(({ key, ids, css }) => (
          <style key={key} data-emotion={`${key} ${ids.join(' ')}`} dangerouslySetInnerHTML={{ __html: css }} />
        ))}
      </head>
      <body>
        <noscript>
          <iframe
            title="Google Tag Manager"
            src={`https://www.googletagmanager.com/ns.html?id=${import.meta.env.VITE_GTM_ID}`}
            height="0"
            width="0"
            style={{ display: 'none', visibility: 'hidden' }}
          ></iframe>
        </noscript>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
});

export { Document };
